import upperCase from "lodash/upperCase";
import Timeago from "react-timeago";
import React from "react";
import lowerCase from "lodash/lowerCase";
import { format, utcToZonedTime } from "date-fns-tz";
import get from "lodash/get";
import { MONTHS } from "../../constants";

export const timestampToFormat = (timestamp, config = {}, languageCode = "en", isYesterday = false) => {
  const { isUpperCase = false, timeFormat = "12hours", localizedZeroToPad, text } = config;
  const monthList = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dateTime = new Date(timestamp);
  const date = dateTime.getDate().toString().padStart(2, 0);

  const month = monthList[dateTime.getMonth()];
  let localizedMonth = month;
  if (isUpperCase) {
    localizedMonth = upperCase(month);
  }

  const year = dateTime.getFullYear();
  const { localizedMeridiem, localizedMonths, direction = "ltr" } = config;
  const time = formatAMPM(timestamp, timeFormat, localizedZeroToPad, languageCode);
  let timeWithLocalizedMeridiem = time;

  if (localizedMonths) {
    localizedMonth = get(localizedMonths, [lowerCase(month)]) || month;
  }
  const localizedDate = Number(date).toLocaleString(languageCode);
  const localizedYear = year.toLocaleString(languageCode).replace(/,/g, "");
  if (localizedMeridiem) {
    const isAm = time.includes("am");
    const { am = "am", pm = "pm" } = localizedMeridiem;
    timeWithLocalizedMeridiem = (isAm ? time.replace("am", am) : time.replace("pm", pm)) || time;
  }

  if (isYesterday) {
    return `Yesterday ${timeWithLocalizedMeridiem} IST`;
  }

  return `${localizedMonth} ${localizedDate}, ${localizedYear} ${timeWithLocalizedMeridiem} IST`;
};

export function formatter(value, unit, suffix, date, config, languageCode) {
  const showDateForUnits = ["day", "week", "month", "year"];
  // suffix here refers to ago
  // unit here refers to seconds, minute, minutes,...
  const localizedSuffix = get(config, ["localizedPublishTime", suffix]) || suffix;
  let localizedUnitOfTime;

  const checkForYesterday = (articleDate) => {
    const timezone = "Asia/Kolkata";
    const now = utcToZonedTime(new Date(), timezone);
    const article = utcToZonedTime(new Date(articleDate), timezone);

    now.setHours(0, 0, 0, 0);
    article.setHours(0, 0, 0, 0);

    const diffTime = now.getTime() - article.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays === 1;
  };

  if ((unit === "hour" && value > 23) || showDateForUnits.indexOf(unit) !== -1) {
    return timestampToFormat(date, config, languageCode);
  } else if (value > 1) {
    if (unit === "hour" && checkForYesterday(date)) {
      return timestampToFormat(date, config, languageCode, true);
    } else if (unit === "second") {
      return `1 min ago`;
    }
    localizedUnitOfTime = unit === "minute" ? "mins" : get(config, ["localizedPublishTime", `${unit}s`]) || `${unit}s`;
    return `${value.toLocaleString(languageCode)} ${localizedUnitOfTime} ${localizedSuffix}`;
  }
  localizedUnitOfTime = get(config, ["localizedPublishTime", unit]) || unit;
  localizedUnitOfTime = localizedUnitOfTime === "minute" ? "min" : localizedUnitOfTime;
  return `${value.toLocaleString(languageCode)} ${localizedUnitOfTime} ${localizedSuffix} `;
}

function formatAMPM(timestamp, timeFormat = "12hours", localizedZeroToPad, languageCode) {
  const dateTime = new Date(timestamp);
  let hours = dateTime.getHours();
  let minutes = dateTime.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours || 12;
  const padZero = languageCode === "en" ? "0" : localizedZeroToPad;
  minutes = minutes < 10 ? padZero + minutes.toLocaleString(languageCode) : minutes.toLocaleString(languageCode);
  const strTime = hours.toLocaleString(languageCode) + ":" + minutes + ampm.toLocaleString(languageCode);
  if (timeFormat === "24hours") {
    return dateTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return strTime;
}
const getTimeDiffUnit = (secondsAgo) => {
  if (secondsAgo < 60) return { value: 1, unit: "minute" }; // force "1 min ago"
  if (secondsAgo < 3600) return { value: Math.floor(secondsAgo / 60), unit: "minute" };
  return { value: Math.floor(secondsAgo / 3600), unit: "hour" };
};

export const getTimeStamp = (
  date,
  formatter,
  config = {},
  languageCode,
  template,
  timezone = "Asia/Kolkata" // default to expected timezone
) => {
  if (timezone && template === "story") {
    const zonedTime = utcToZonedTime(date, timezone);
    const formatZonedTime = format(zonedTime, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: timezone });
    const timeStamp = timestampToFormat("", "", "", date, config);
    return <time dateTime={formatZonedTime}>{timeStamp}</time>;
  }

  const zonedTime = utcToZonedTime(date, timezone); // story time in local
  const isoDate = format(zonedTime, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: timezone });

  const now = new Date();
  const localNow = utcToZonedTime(now, timezone);

  const sameDay =
    zonedTime.getDate() === localNow.getDate() &&
    zonedTime.getMonth() === localNow.getMonth() &&
    zonedTime.getFullYear() === localNow.getFullYear();

  const yesterday =
    localNow.getDate() - zonedTime.getDate() === 1 &&
    zonedTime.getMonth() === localNow.getMonth() &&
    zonedTime.getFullYear() === localNow.getFullYear();

  if (sameDay) {
    const diffInSeconds = Math.floor((localNow - zonedTime) / 1000);
    const { value, unit } = getTimeDiffUnit(diffInSeconds);
    const preRendered = formatter(value < 1 ? 1 : value, unit, "ago", date, config, languageCode);
    return <time dateTime={isoDate}>{preRendered}</time>;
  } else {
    const timeStamp = timestampToFormat(zonedTime, config, languageCode, yesterday);
    return <time dateTime={isoDate}>{timeStamp}</time>;
  }
};

export const getMagazinesTimeStamp = (timestamp) => {
  if (!timestamp) return "";

  const dateTime = new Date(timestamp);

  const month = MONTHS[dateTime.getMonth()];

  const year = dateTime.getFullYear();

  return `${month} ${year}`;
};
